import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/id';
import 'dayjs/locale/th';
import 'dayjs/locale/vi';
import 'dayjs/locale/zh-tw';

dayjs.extend(utc);
dayjs.extend(localizedFormat);
dayjs.extend(relativeTime);

const DAYJS_LOCALE_MAP: Record<string, string> = {
  en: 'en',
  id: 'id',
  th: 'th',
  vi: 'vi',
  zh: 'zh-tw',
};

/** Map i18n language code to a dayjs locale id. */
export function resolveDayjsLocale(language: string): string {
  const code = language.split('-')[0];
  return DAYJS_LOCALE_MAP[code] ?? 'en';
}

/** Apply dayjs locale synchronously (call during render before formatting). */
export function syncDayjsLocale(language: string): string {
  const locale = resolveDayjsLocale(language);
  if (dayjs.locale() !== locale) {
    dayjs.locale(locale);
  }
  return locale;
}

const FORMAT_TOKEN_PATTERN = /\bLLLL\b|\bLLL\b|\bLL\b|\bLTS\b|\bLT\b|\bL\b/g;

/** Expand localized tokens (L, LL, LT, …) to the active locale's pattern. */
export function resolveLocalizedFormat(
  format: string,
  localeName: string = dayjs.locale(),
): string {
  const formats = dayjs.Ls[localeName]?.formats as Record<string, string> | undefined;
  if (!formats) return format;

  return format.replace(FORMAT_TOKEN_PATTERN, (token) => formats[token] ?? token);
}

/**
 * Locale-aware format tokens (dayjs localizedFormat plugin).
 * Actual patterns come from the active dayjs locale — set in AntdConfigProvider.
 */
export const DATE_FORMAT = 'LL';
export const TIME_FORMAT = 'LT';
export const DATE_TIME_FORMAT = 'LL LTS';
/** Weekday + long date + time, e.g. overview panels. */
export const DATE_TIME_FULL_FORMAT = 'LLLL';
/** Compact date for picker inputs. */
export const PICKER_DATE_FORMAT = 'L';
export const PICKER_DATE_TIME_FORMAT = 'L LTS';

/** Locale-aware picker patterns for the active i18n language. */
export function getPickerLocalizedFormats(language: string): {
  date: string;
  dateTime: string;
} {
  const locale = syncDayjsLocale(language);
  return {
    date: resolveLocalizedFormat(PICKER_DATE_FORMAT, locale),
    dateTime: resolveLocalizedFormat(PICKER_DATE_TIME_FORMAT, locale),
  };
}

export type DateInput = string | number | Date | null | undefined;

/** Placeholder rendered when the value is empty/invalid. */
export const EMPTY_DATE = '-';

/**
 * Format a date/time value with dayjs.
 * Returns `fallback` when the value is missing or not a valid date.
 */
export function formatDateTime(
  value: DateInput,
  format: string = DATE_TIME_FORMAT,
  fallback: string = EMPTY_DATE
): string {
  if (value === null || value === undefined || value === '') return fallback;
  const d = dayjs(value);
  return d.isValid() ? d.format(resolveLocalizedFormat(format)) : fallback;
}

/** Date only, locale-aware long date. */
export function formatDate(value: DateInput, fallback: string = EMPTY_DATE): string {
  return formatDateTime(value, DATE_FORMAT, fallback);
}

export function formatDateHistory(value: DateInput): string {
  if (value === null || value === undefined || value === '') return EMPTY_DATE;
  return dayjs(value).format(resolveLocalizedFormat('LL LTS'));
}
/** Time only, locale-aware. */
export function formatTime(value: DateInput, fallback: string = EMPTY_DATE): string {
  return formatDateTime(value, TIME_FORMAT, fallback);
}

/** ISO UTC start of day for API date-range filters. */
export function toFilterRangeStart(date: dayjs.Dayjs): string {
  return `${date.format('YYYY-MM-DD')}T00:00:00Z`;
}

/** ISO UTC end of day for API date-range filters. */
export function toFilterRangeEnd(date: dayjs.Dayjs): string {
  return `${date.format('YYYY-MM-DD')}T23:59:59Z`;
}

/**
 * Parse filter ISO strings into a RangePicker value tuple.
 *
 * When `dateOnly` is true (default), `from`/`to` are expected to be the
 * UTC day-boundary ISO strings produced by toFilterRangeStart/End (e.g.
 * `...T23:59:59Z`). The UTC calendar day is extracted and re-anchored to
 * the same local calendar day so the picker always shows the day the user
 * picked — a plain `dayjs(value)` renders in local time and can roll the
 * date forward/back a day for any timezone offset from UTC.
 *
 * Pass `dateOnly: false` for full-instant values (e.g. `toISOString()`),
 * which are parsed as-is.
 */
export function parseFilterDateRange(
  from?: string,
  to?: string,
  dateOnly = true
): [dayjs.Dayjs | null, dayjs.Dayjs | null] | null {
  if (!from && !to) return null;

  const parse = (value?: string): dayjs.Dayjs | null => {
    if (!value) return null;
    return dateOnly ? dayjs(dayjs.utc(value).format('YYYY-MM-DD')) : dayjs(value);
  };

  return [parse(from), parse(to)];
}

/** Relative time, e.g. "3 hours ago". */
export function formatFromNow(value: DateInput, fallback: string = EMPTY_DATE): string {
  if (value === null || value === undefined || value === '') return fallback;
  const d = dayjs(value);
  return d.isValid() ? d.fromNow() : fallback;
}
